import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormServerSideErrorsHandler } from "../components/form/ErrorsHandler/ErrorsHandler";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import { draftApiSchema } from "../schemas/draft";

export const draftsApi = {
    getDrafts: async (axios, params) => {
        return axios
            .get(apiUrls.drafts.all, { params })
            .then((response) => draftApiSchema.getDraftsResponse.parse(response.data));
    },

    generateMail: async (axios, draftId) => {
        return axios
            .get(apiUrls.drafts.generateMail(draftId))
            .then((response) => draftApiSchema.generateMailResponse.parse(response.data));
    },
    generateNotificationMail: async (axios, draftId, body) => {
        return axios.post(
            apiUrls.drafts.generateNotificationMail(draftId),
            body
        );
    },
    generateOffer: async (axios, draftId, body) => {
        return axios.post(
            apiUrls.drafts.generateOffer(draftId),
            // draftApiSchema.generateOfferPdfRequest.parse(body),
            body,
            { responseType: "blob" },
        );
    },
    generateBlankOffer: async (axios, draftId, body) => {
        return axios.post(
            apiUrls.drafts.generateBlankOffer(draftId),
            body,
            { responseType: "blob" },
        );
    },
    generateAgreement: async (axios, draftId, body) => {
        return axios.post(
            apiUrls.drafts.generateAgreement(draftId),
            draftApiSchema.generateAgreementPdfRequest.parse(body),
            { responseType: "blob" },
        );
    },
    generateAgreementDocx: async (axios, draftId, body) => {
        return axios.post(
            apiUrls.drafts.generateAgreementDocx(draftId),
            draftApiSchema.generateAgreementPdfRequest.parse(body),
            { responseType: "blob" },
        );
    },
    downloadDraft: async (axios, draftId) => {
        return axios
            .get(apiUrls.drafts.downloadDraft(draftId), { responseType: "blob" })
            .then((response) => response.data);
    },
    updateOfferDraft: async (axios, draftId, data) => {
        return axios
            .patch(apiUrls.drafts.update(draftId), data)
            .then((response) => draftApiSchema.updateOfferDraftRequest.parse(response.data));
    },
    createDocxDraft: async (axios, data) => {
        const formData = new FormData();
        formData.append("nr", data.nr);
        formData.append("name", data.name);
        formData.append("description", data.description);
        formData.append("language", data.language);
        formData.append("content", data.content);
        formData.append("draft_active", String(data.active));
        formData.append("is_offer", String(data.is_offer));
        formData.append("is_agreement", String(data.is_agreement));

        return axios
            .post(apiUrls.drafts.all, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            .then((response) => draftApiSchema.createDocxDraftResponse.parse(response.data));
    },

    createEmailDraft: async (axios, data) => {
        return axios
            .post(apiUrls.drafts.all, { ...data, type: "mail" })
            .then((response) => draftApiSchema.emailDraftResponse.parse(response.data));
    },

    updateEmailDraft: async (axios, draftId, data) => {
        return axios
            .patch(apiUrls.drafts.update(draftId), data)
            .then((response) => draftApiSchema.emailDraftResponse.parse(response.data));
    },

    deleteDraft: async (axios, draftId) => {
        return axios.delete(apiUrls.drafts.delete(draftId));
    },
};

export function useDrafts(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["drafts", Object.fromEntries(params.entries())],
        queryFn: () => draftsApi.getDrafts(axiosPriv, params),
    });
}

export function useEmailDrafts(params = new URLSearchParams(), options = {}) {
    const emailParams = new URLSearchParams(params);
    emailParams.set("type", "mail");
    return useDrafts(emailParams, options);
}

export function useNotificationDrafts(params = new URLSearchParams(), options = {}) {
    const notificationParams = new URLSearchParams(params);
    notificationParams.set("type", "notification");
    return useDrafts(notificationParams, options);
}

export function useOfferDrafts(params = new URLSearchParams(), options = {}) {
    const offerParams = new URLSearchParams(params);
    offerParams.set("type", "offer");
    return useDrafts(offerParams, options);
}

export function useAgreementDrafts(params = new URLSearchParams(), options = {}) {
    const searchParams = new URLSearchParams(params);
    searchParams.set("type", "agreement");
    return useDrafts(searchParams, options);
}

export function useGenerateMailDraft(draftId, options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["drafts", "generateMail", draftId],
        queryFn: () => draftsApi.generateMail(axiosPriv, draftId),
        enabled: !!draftId,
    });
}

export function useGenerateNotificationMailDraft(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        mutationFn: (body) => draftsApi.generateNotificationMail(axiosPriv, body.draftId, body.requestBody),
        onError: onServerError,
        onSuccess: (data, variables, context) => {
            options.onSuccess?.(data, variables, context);
        },
    });
}

export function useDownloadDraft(draftId, options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["drafts", "download", draftId],
        queryFn: () => draftsApi.downloadDraft(axiosPriv, draftId),
        enabled: !!draftId,
    });
}

export function useGenerateOfferDraftMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        mutationFn: (body) => draftsApi.generateOffer(axiosPriv, body.draftId, body.requestBody),
        onError: onServerError,
    });
}

    export function useGenerateBlankOfferDraftMutation(options = {}) {
        const axiosPriv = useAxiosPrivate();
        const { onServerError } = useFormServerSideErrorsHandler();
        return useMutation({
            ...options,
            mutationFn: (body) => draftsApi.generateBlankOffer(axiosPriv, body.draftId, body.requestBody),
            onError: onServerError,
        });
    }

export function useGenerateAgreementDraftMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        mutationFn: (body) =>
            draftsApi.generateAgreement(axiosPriv, body.draftId, body.requestBody),
        onError: onServerError,
    });
}

export function useGenerateAgreementDocxMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        mutationFn: (body) =>
            draftsApi.generateAgreementDocx(axiosPriv, body.draftId, body.requestBody),
        onError: onServerError,
    });
}

export function useDownloadDraftMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        mutationFn: ({ draftId }) => draftsApi.downloadDraft(axiosPriv, draftId),
        onError: onServerError,
    });
}

export function useUpdateOfferDraftMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["drafts"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ draftId, data }) => draftsApi.updateOfferDraft(axiosPriv, draftId, data),
        onError: onServerError,
    });
}

export function useCreateDocxDraftMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["drafts"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (data) => draftsApi.createDocxDraft(axiosPriv, data),
        onError: onServerError,
    });
}

export function useDeleteDraftMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["drafts"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ draftId }) => draftsApi.deleteDraft(axiosPriv, draftId),
        onError: onServerError,
    });
}

export function useCreateEmailDraftMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["drafts"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (data) => draftsApi.createEmailDraft(axiosPriv, data),
        onError: onServerError,
    });
}

export function useUpdateEmailDraftMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["drafts"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ draftId, data }) => draftsApi.updateEmailDraft(axiosPriv, draftId, data),
        onError: onServerError,
    });
}
